import React, { useEffect, useState, useRef, useCallback } from 'react';
import Swal from 'sweetalert2';
import {
  Row,
  Col,
  Label,
  Button,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Input,
  Card,
  CardBody,
  Spinner,
  Table,
} from 'reactstrap';
import 'react-datepicker/dist/react-datepicker.css';
import 'flatpickr/dist/themes/material_blue.css';
import 'flatpickr/dist/plugins/monthSelect/style.css';
import axios from 'axios';
import { api } from '../../config';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import logger from '../../utils/logger';

axios.defaults.withCredentials = true;
axios.defaults.xsrfCookieName = 'XSRF-TOKEN';
axios.defaults.xsrfHeaderName = 'X-XSRF-TOKEN';

const SupportDocumentTab = ({
  _goNext,
  goBack,
  ids,
  form,
  _setForm,
  handleSaveDraftProc,
  handleSaveCompleteDoc,
  token,
  setIsLoading,
  _isLoading,
}) => {
  const [uploadDocument, setUploadDocument] = useState({ items: [] });
  const [fileCategory, setFileCategory] = useState({ items: [] });
  const [localLoading, setLocalLoading] = useState(false);
  const fileInputRef = useRef(null);
  const [dragOver, setDragOver] = useState(false);
  const [masterDocModal, setMasterDocModal] = useState(false);
  const [selectedMasterDocIds, setSelectedMasterDocIds] = useState([]);
  const [uploadModal, setUploadModal] = useState(false);
  const [pendingFile, setPendingFile] = useState(null);
  const [customFileName, setCustomFileName] = useState('');

  const fetchDocumentUpload = useCallback(
    async procurementId => {
      try {
        const response = await fetch(
          `${api.API_URL}/procurement/get-procurement-document?ProcurementId=${procurementId}`,
          {
            params: { ProcurementId: procurementId },
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`,
            },
          }
        );
        const result = await response.json();
        const items = result.data.items.map(item => ({
          id: item.id ?? null,
          customFileName: item.customFileName
            ? item.customFileName.replace(/\.[^/.]+$/, '')
            : null,
          documentName: item.documentUpload?.documentName ?? null,
          documentUploadId: item.documentUploadId ?? null,
          actualUrl: item.link ?? null,
          isMasterDocument: !!item.documentUploadId,
          isShowToDMUT: item.isShowToDMUT ?? false,
          lastUpdated: item.updatedDate
            ? new Date(item.updatedDate).toLocaleDateString('en-GB', {
                day: '2-digit',
                month: 'short',
                year: 'numeric',
              })
            : null,
          firstCreated: item.createdDate
            ? new Date(item.createdDate).toLocaleDateString('en-GB', {
                day: '2-digit',
                month: 'short',
                year: 'numeric',
              })
            : null,
        }));
        setUploadDocument({ items });
      } catch (error) {
        logger.error('Error fetching data from Invoice API:', error);
      }
    },
    [token]
  );

  const fetchFileCategory = useCallback(async () => {
    try {
      const response = await fetch(
        `${api.API_URL}/document-upload/getall-master-procurement-document`,
        {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
        }
      );
      const result = await response.json();
      const items = result.data.items.map(item => ({
        value: item.id,
        label: item.documentName,
      }));
      setFileCategory({ items });
    } catch (error) {
      logger.error('Error fetching file categories:', error);
    }
  }, [token]);

  useEffect(() => {
    if (ids) {
      fetchDocumentUpload(ids);
      fetchFileCategory();
    }
  }, [ids, fetchDocumentUpload, fetchFileCategory]);

  const handleOpenMasterDocModal = () => {
    setSelectedMasterDocIds([]);
    setMasterDocModal(true);
  };

  const handleMasterDocCheckboxToggle = id => {
    setSelectedMasterDocIds(prev =>
      prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
    );
  };

  const handleMasterDocSelectAll = () => {
    const availableItems = fileCategory.items.filter(
      item =>
        !uploadDocument.items.some(doc => doc.documentUploadId === item.value)
    );
    const allIds = availableItems.map(item => item.value);
    setSelectedMasterDocIds(prev =>
      prev.length === allIds.length ? [] : allIds
    );
  };

  const handleAddMasterDoc = async () => {
    if (selectedMasterDocIds.length === 0) return;
    setLocalLoading(true);
    try {
      const payload = {
        procurementId: ids,
        documentUploadIds: selectedMasterDocIds,
      };

      await axios.put(
        `${api.API_URL}/procurement-document/select-master-document`,
        payload,
        {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
        }
      );
      toast.success(
        `${selectedMasterDocIds.length} document(s) have been added`
      );
      setMasterDocModal(false);
      setSelectedMasterDocIds([]);
      fetchDocumentUpload(ids);
    } catch (error) {
      toast.error(`Failed to add document(s): ${error.message}`);
    } finally {
      setLocalLoading(false);
    }
  };

  const handleFileSelect = e => {
    const file = e.target.files[0];
    if (!file) return;
    setPendingFile(file);
    setCustomFileName('');
    setUploadModal(true);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleDrop = e => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);
    const file = e.dataTransfer.files[0];
    if (!file) return;
    setPendingFile(file);
    setCustomFileName('');
    setUploadModal(true);
  };

  const handleDragOver = e => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(true);
  };

  const handleDragLeave = e => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);
  };

  const handleUploadFile = async () => {
    if (!pendingFile || !customFileName.trim()) return;
    setLocalLoading(true);
    try {
      const formData = new FormData();
      formData.append('ProcurementId', ids);
      formData.append('FileName', customFileName.trim());
      formData.append('FileAttachment', pendingFile);
      await axios.post(
        `${api.API_URL}/procurement-document/custom-upload`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
            Authorization: `Bearer ${token}`,
          },
        }
      );
      toast.success('File has been uploaded');
      setUploadModal(false);
      setPendingFile(null);
      setCustomFileName('');
      fetchDocumentUpload(ids);
    } catch (error) {
      toast.error(`Failed to upload file: ${error.message}`);
    } finally {
      setLocalLoading(false);
    }
  };

  const handleToggleDmut = async (id, checked) => {
    setUploadDocument(prev => ({
      items: prev.items.map(item =>
        item.id === id ? { ...item, isShowToDMUT: checked } : item
      ),
    }));
    try {
      await axios.put(
        `${api.API_URL}/procurement-document/change-is-show-to-dmut/${id}`,
        { isShowToDmut: checked },
        {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
        }
      );
    } catch (error) {
      setUploadDocument(prev => ({
        items: prev.items.map(item =>
          item.id === id ? { ...item, isShowToDMUT: !checked } : item
        ),
      }));
      toast.error(`Failed to update DMUT visibility: ${error.message}`);
    }
  };

  const handleDirectOpenNewTab = url => {
    if (!url) {
      toast.info('No file available for this document');
      return;
    }
    const link = document.createElement('a');
    link.href = url;
    link.target = '_blank';
    link.rel = 'noopener noreferrer';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleConfirmDelFileUpload = id => {
    Swal.fire({
      title: 'Are You sure want to Delete this Record?',
      text: 'Deleted Data cannot be Recovered!!',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, Delete!',
      cancelButtonText: 'Cancel',
    }).then(result => {
      if (result.isConfirmed) {
        handleDelFileUpload(id);
      }
    });
  };

  const handleDelFileUpload = async id => {
    try {
      setIsLoading(true);
      await axios.delete(`${api.API_URL}/procurement-document/${id}`, {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });
      toast.success('Data has been deleted');
    } catch (error) {
      toast.error(`Failed to delete data: ${error.message}`);
    } finally {
      setIsLoading(false);
      fetchDocumentUpload(ids);
    }
  };

  return (
    <React.Fragment>
      <div className='d-flex justify-content-between align-items-center mb-4'>
        <div>
          <h5
            className='fw-semibold mb-1'
            style={{ color: 'var(--vz-heading-color, #1a1a2e)' }}
          >
            <i className='ri-file-list-3-line me-2'></i>
            Document Information
          </h5>
          <p className='text-muted mb-0' style={{ fontSize: '12px' }}>
            Manage supporting documents for this procurement
          </p>
        </div>
      </div>

      <Card className='border shadow-sm'>
        <CardBody>
          <div>
            {/* SECTION 1 - UPLOAD CARDS */}
            <div className='mb-3'>
              <div>
                <Row>
                  <Col
                    sm={3}
                    style={{
                      pointerEvents:
                        form.isProcurementCompleted !== false ? 'none' : 'auto',
                      opacity: form.isProcurementCompleted !== false ? 0.5 : 1,
                    }}
                  >
                    <h6
                      className='text-uppercase fw-semibold mb-3'
                      style={{
                        color: 'var(--vz-heading-color, #1a1a2e)',
                        fontSize: '12px',
                        letterSpacing: '0.5px',
                      }}
                    >
                      Upload Document
                    </h6>
                    <div
                      className='sd-upload-card mb-3'
                      onClick={
                        form.isProcurementCompleted !== false
                          ? undefined
                          : handleOpenMasterDocModal
                      }
                    >
                      <div className='sd-upload-card-icon master'>
                        <i className='ri-database-2-line'></i>
                      </div>
                      <div className='sd-upload-card-text'>
                        <h6>Master Document</h6>
                        <p>Select from master database</p>
                      </div>
                    </div>
                    <div
                      className={`sd-upload-card${dragOver ? ' drag-over' : ''}`}
                      onDragOver={
                        form.isProcurementCompleted !== false
                          ? undefined
                          : handleDragOver
                      }
                      onDragLeave={
                        form.isProcurementCompleted !== false
                          ? undefined
                          : handleDragLeave
                      }
                      onDrop={
                        form.isProcurementCompleted !== false
                          ? undefined
                          : handleDrop
                      }
                      onClick={() => {
                        if (form.isProcurementCompleted !== false) return;
                        fileInputRef.current?.click();
                      }}
                    >
                      <div className='sd-upload-card-icon custom'>
                        <i className='ri-upload-cloud-2-line'></i>
                      </div>
                      <div className='sd-upload-card-text'>
                        <h6>Upload Custom File</h6>
                        <p>Browse from your device</p>
                      </div>
                      <input
                        ref={fileInputRef}
                        type='file'
                        hidden
                        accept='.pdf,.doc,.docx,.xls,.xlsx'
                        onChange={handleFileSelect}
                      />
                    </div>
                  </Col>

                  <Col sm={9}>
                    <div>
                      <h6
                        className='text-uppercase fw-semibold mb-3'
                        style={{
                          color: 'var(--vz-heading-color, #1a1a2e)',
                          fontSize: '12px',
                          letterSpacing: '0.5px',
                        }}
                      >
                        Selected / Uploaded Document
                      </h6>
                      {uploadDocument.items.length === 0 ? (
                        <div className='sd-empty'>
                          <i className='ri-file-text-line'></i>
                          <p>No documents uploaded yet</p>
                        </div>
                      ) : (
                        <>
                          <div className='sd-doc-header'>
                            <span style={{ flex: '2 1 0' }}>Document Name</span>
                            <span style={{ flex: '1 1 0' }}>Source</span>
                            <span style={{ flex: '1 1 0' }}>Last Updated</span>
                            <span
                              style={{ flex: '1 1 0', textAlign: 'center' }}
                            >
                              Show to DMUT?
                            </span>
                            <span
                              style={{ flex: '1.4 1 0', textAlign: 'right' }}
                            >
                              Action
                            </span>
                          </div>
                          {uploadDocument.items.map((doc, index) => (
                            <div key={doc.id || index} className='sd-doc-row'>
                              <div style={{ flex: '2 1 0' }}>
                                <span
                                  className='fw-semibold'
                                  style={{
                                    fontSize: '13px',
                                    color: 'var(--vz-heading-color, #1a1a2e)',
                                  }}
                                >
                                  {doc.documentName ||
                                    doc.customFileName ||
                                    '-'}
                                </span>
                              </div>
                              <div style={{ flex: '1 1 0' }}>
                                <span
                                  className={`sd-badge ${doc.isMasterDocument ? 'master' : 'custom'}`}
                                >
                                  {doc.isMasterDocument
                                    ? 'Master Document'
                                    : 'Upload Custom'}
                                </span>
                              </div>
                              <div style={{ flex: '1 1 0' }}>
                                <span
                                  style={{
                                    fontSize: '12px',
                                    color: 'var(--vz-secondary-color, #6b7280)',
                                  }}
                                >
                                  {doc.lastUpdated || doc.firstCreated || 'N/A'}
                                </span>
                              </div>

                              <div className='d-flex form-check form-switch form-switch-secondary mb-2'>
                                <div className='flex-shrink-0'>
                                  <Input
                                    className='form-check-input'
                                    type='checkbox'
                                    checked={doc.isShowToDMUT || false}
                                    onChange={e =>
                                      handleToggleDmut(doc.id, e.target.checked)
                                    }
                                    disabled={
                                      form.isProcurementCompleted !== false
                                    }
                                  />
                                </div>
                              </div>

                              <div
                                style={{ flex: '1.4 1 0', textAlign: 'right' }}
                              >
                                <div className='d-flex gap-1 justify-content-end'>
                                  <button
                                    className='sd-btn-ghost'
                                    onClick={() =>
                                      handleDirectOpenNewTab(doc.actualUrl)
                                    }
                                  >
                                    <i className='ri-eye-line'></i> View
                                  </button>
                                  {form.isProcurementCompleted === false && (
                                    <button
                                      className='sd-btn-ghost danger'
                                      onClick={() =>
                                        handleConfirmDelFileUpload(doc.id)
                                      }
                                    >
                                      <i className='ri-delete-bin-6-line'></i>{' '}
                                      Delete
                                    </button>
                                  )}
                                </div>
                              </div>
                            </div>
                          ))}
                        </>
                      )}
                    </div>

                    {/* NAVIGATION */}
                  </Col>
                </Row>
              </div>
            </div>

            {/* SECTION 2 - DOCUMENT LIST */}
          </div>
        </CardBody>
      </Card>
      <div className='req-actionbar d-print-none'>
        <button
          type='button'
          className='btn btn-light btn-label me-auto'
          onClick={goBack}
        >
          <i className='ri-arrow-left-line label-icon align-middle fs-16 me-2'></i>
          Back to Requirement
        </button>
        {!form.isSupportingDocumentsCompleted && (
          <button
            type='button'
            className='btn btn-light btn-label'
            onClick={handleSaveDraftProc}
          >
            <i className='ri-stack-line label-icon align-middle fs-16 me-2'></i>
            Save as Draft
          </button>
        )}
        {form.isProcurementCompleted === false && (
          <Button
            color='primary'
            className='btn-label right'
            onClick={handleSaveCompleteDoc}
          >
            <i className='ri-login-box-line label-icon align-middle fs-16 ms-2'></i>
            Save & Complete
          </Button>
        )}
      </div>

      {/* MASTER DOCUMENT MODAL */}
      <Modal
        isOpen={masterDocModal}
        toggle={() => setMasterDocModal(false)}
        centered
        size='md'
      >
        <ModalHeader
          toggle={() => setMasterDocModal(false)}
          className='fw-semibold'
          style={{ borderBottom: '1px solid var(--vz-border-color, #e5e7eb)' }}
        >
          <i
            className='ri-database-2-line me-2'
            style={{ color: '#0d7c66' }}
          ></i>
          Select Master Document
        </ModalHeader>
        <ModalBody className='py-4'>
          <div className='mb-2'>
            <Label
              className='form-label fw-medium'
              style={{
                fontSize: '13px',
                color: 'var(--vz-body-color, #374151)',
              }}
            >
              Document Category
            </Label>
            <div
              className='table-responsive'
              style={{ maxHeight: '300px', overflowY: 'auto' }}
            >
              {(() => {
                const availableMasterDocs = fileCategory.items.filter(
                  item =>
                    !uploadDocument.items.some(
                      doc => doc.documentUploadId === item.value
                    )
                );
                if (availableMasterDocs.length === 0) {
                  return (
                    <p
                      className='text-muted text-center py-3 mb-0'
                      style={{ fontSize: '13px' }}
                    >
                      No master documents available
                    </p>
                  );
                }
                return (
                  <Table className='align-middle table-nowrap mb-0' size='sm'>
                    <thead className='table-light'>
                      <tr>
                        <th className='text-center' style={{ width: '50px' }}>
                          <div className='form-check'>
                            <Input
                              className='form-check-input'
                              type='checkbox'
                              checked={
                                availableMasterDocs.length > 0 &&
                                selectedMasterDocIds.length ===
                                  availableMasterDocs.length
                              }
                              ref={el => {
                                if (el)
                                  el.indeterminate =
                                    selectedMasterDocIds.length > 0 &&
                                    selectedMasterDocIds.length <
                                      availableMasterDocs.length;
                              }}
                              onChange={handleMasterDocSelectAll}
                            />
                          </div>
                        </th>
                        <th>Document Name</th>
                      </tr>
                    </thead>
                    <tbody>
                      {availableMasterDocs.map(item => (
                        <tr key={item.value}>
                          <td className='text-center'>
                            <div className='form-check'>
                              <Input
                                className='form-check-input'
                                type='checkbox'
                                checked={selectedMasterDocIds.includes(
                                  item.value
                                )}
                                onChange={() =>
                                  handleMasterDocCheckboxToggle(item.value)
                                }
                              />
                            </div>
                          </td>
                          <td>
                            <div className='d-flex align-items-center gap-2'>
                              <i
                                className='ri-folder-2-line'
                                style={{ color: '#f59e0b', fontSize: '16px' }}
                              ></i>
                              <span className='flex-grow-1'>{item.label}</span>
                              <button
                                type='button'
                                className='sd-btn-ghost'
                                onClick={() =>
                                  toast.info(
                                    'No file available for this document'
                                  )
                                }
                                title='View'
                              >
                                <i className='ri-eye-line'></i> View
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                );
              })()}
            </div>
          </div>
        </ModalBody>
        <ModalFooter
          style={{ borderTop: '1px solid var(--vz-border-color, #e5e7eb)' }}
        >
          <Button
            color='secondary'
            size='sm'
            outline
            onClick={() => setMasterDocModal(false)}
          >
            Cancel
          </Button>
          <Button
            color='primary'
            size='sm'
            disabled={selectedMasterDocIds.length === 0 || localLoading}
            onClick={handleAddMasterDoc}
            style={{ backgroundColor: '#0d7c66', borderColor: '#0d7c66' }}
          >
            {localLoading ? (
              <>
                <Spinner size='sm' className='me-1' /> Adding...
              </>
            ) : (
              <>
                <i className='ri-database-2-line me-1'></i> Add Document
              </>
            )}
          </Button>
        </ModalFooter>
      </Modal>

      {/* UPLOAD CATEGORY MODAL */}
      <Modal
        isOpen={uploadModal}
        toggle={() => setUploadModal(false)}
        centered
        size='md'
      >
        <ModalHeader
          toggle={() => setUploadModal(false)}
          className='fw-semibold'
          style={{ borderBottom: '1px solid var(--vz-border-color, #e5e7eb)' }}
        >
          <i
            className='ri-upload-cloud-2-line me-2'
            style={{ color: '#e8860c' }}
          ></i>
          Upload Document
        </ModalHeader>
        <ModalBody className='py-4'>
          <div className='mb-3'>
            <Label
              className='form-label fw-medium'
              style={{
                fontSize: '13px',
                color: 'var(--vz-body-color, #374151)',
              }}
            >
              Selected File
            </Label>
            <div
              className='d-flex align-items-center gap-2 p-2 rounded'
              style={{
                background: 'var(--vz-light, #f9fafb)',
                border: '1px solid var(--vz-border-color, #e5e7eb)',
              }}
            >
              <i
                className='ri-file-text-line'
                style={{ color: 'var(--vz-secondary-color, #6b7280)' }}
              ></i>
              <span
                className='fw-medium text-truncate'
                style={{ fontSize: '13px' }}
              >
                {pendingFile?.name}
              </span>
              <span
                style={{
                  fontSize: '11px',
                  color: 'var(--vz-secondary-color, #9ca3af)',
                  marginLeft: 'auto',
                }}
              >
                {(pendingFile?.size / 1024).toFixed(1)} KB
              </span>
            </div>
          </div>
          <div className='mb-2'>
            <Label
              className='form-label fw-medium'
              style={{
                fontSize: '13px',
                color: 'var(--vz-body-color, #374151)',
              }}
            >
              Document Name
            </Label>
            <Input
              type='text'
              placeholder='Enter document name...'
              value={customFileName}
              onChange={e => setCustomFileName(e.target.value)}
            />
          </div>
        </ModalBody>
        <ModalFooter
          style={{ borderTop: '1px solid var(--vz-border-color, #e5e7eb)' }}
        >
          <Button
            color='secondary'
            size='sm'
            outline
            onClick={() => setUploadModal(false)}
          >
            Cancel
          </Button>
          <Button
            color='primary'
            size='sm'
            disabled={!customFileName.trim() || localLoading}
            onClick={handleUploadFile}
            style={{ backgroundColor: '#0d7c66', borderColor: '#0d7c66' }}
          >
            {localLoading ? (
              <>
                <Spinner size='sm' className='me-1' /> Uploading...
              </>
            ) : (
              <>
                <i className='ri-upload-cloud-2-line me-1'></i> Upload
              </>
            )}
          </Button>
        </ModalFooter>
      </Modal>
    </React.Fragment>
  );
};

export default SupportDocumentTab;
